import React, { useEffect, useState } from "react";
import moment from "moment";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import PopUpComponent from "../../components/PopUp/PopUpComponent";
import PdfSuratJalanManual from "../../components/PdfSuratJalanManual/PdfSuratJalanManual";
import CloseLogo from "../../Assets/close_icon.png";
import { postAxios } from "../../util/apiCall";
import { dataCustomer } from "../../util/dataCustomerSj";

const today = () => moment().utcOffset("+0700").format("YYYY-MM-DD");
const newItem = () => ({
  quantity: "",
  satuan: "PCS",
  namaBarang: "",
  keterangan: "",
});
const safeName = (s) => String(s).replace(/[\\/:*?"<>|]/g, "-");

export default function PoSuratJalanManualPopup({
  dataUser,
  onClose,
  onCreated,
}) {
  const [date, setDate] = useState(today());
  const [noPol, setNoPol] = useState("");

  // tujuan: terdaftar (ambil dari dataCustomer) atau isi manual
  const [isRegistered, setIsRegistered] = useState(true);
  const [customerCode, setCustomerCode] = useState("");
  const [yth, setYth] = useState("");
  const [attnNama, setAttnNama] = useState(""); // nama orang (opsional)
  const [attn, setAttn] = useState(""); // alamat
  const [telp, setTelp] = useState("");

  const [items, setItems] = useState([newItem()]);

  const [previewNo, setPreviewNo] = useState(null);
  const [created, setCreated] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // terima bentuk respon { noSj } maupun { data: { noSj } }
  const previewNoSj =
    previewNo && (previewNo.noSj || (previewNo.data && previewNo.data.noSj));
  const createdNoSj =
    created && (created.noSj || (created.data && created.data.noSj));

  /* ---- nomor SJ otomatis: tampilkan nomor berikutnya (belum dipakai) ---- */
  useEffect(() => {
    setPreviewNo(null);
    setPreviewFailed(false);
    if (!date) return;
    postAxios(
      `${process.env.REACT_APP_ENDPOINT}/suratJalanManual`,
      { tim: dataUser.tim, type: "preview", date },
      dataUser.auth,
      setPreviewNo,
      ""
    );
    const t = setTimeout(() => setPreviewFailed(true), 6000);
    return () => clearTimeout(t);
  }, [date]);

  /* ---- tujuan ---- */
  const toggleRegistered = () => {
    setIsRegistered((v) => !v);
    setCustomerCode("");
    setYth("");
    setAttnNama("");
    setAttn("");
    setTelp("");
  };

  const pickCustomer = (code) => {
    setCustomerCode(code);
    const c = dataCustomer.find((x) => x.customer === code);
    setYth(c ? c.name : "");
    setAttn(c ? c.alamat : "");
    setTelp(c ? c.tlp : "");
  };

  /* ---- item ---- */
  const updateItem = (index, key, value) =>
    setItems((prev) =>
      prev.map((it, i) => (i === index ? { ...it, [key]: value } : it))
    );
  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = () =>
    setItems((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));

  /* ---- simpan: nomor final dibuat backend, lalu PDF diunduh ---- */
  const submit = () => {
    setError("");
    if (!date) return setError("Tanggal wajib diisi.");
    if (!noPol.trim()) return setError("No. Pol wajib diisi.");
    if (isRegistered && !customerCode)
      return setError("Pilih customer tujuan.");
    if (!yth.trim()) return setError("Tujuan (Yth.) wajib diisi.");

    // baris yang benar-benar kosong dibuang
    const filled = items.filter(
      (i) => i.quantity.trim() || i.namaBarang.trim() || i.keterangan.trim()
    );
    if (filled.length === 0) return setError("Isi minimal satu item.");
    const bad = filled.findIndex(
      (i) => !i.quantity.trim() || !i.satuan.trim() || !i.namaBarang.trim()
    );
    if (bad !== -1)
      return setError(
        `Item ke-${bad + 1}: quantity, satuan, dan nama barang wajib diisi.`
      );

    setSubmitting(true);
    postAxios(
      `${process.env.REACT_APP_ENDPOINT}/suratJalanManual`,
      {
        tim: dataUser.tim,
        type: "create",
        date,
        noPol: noPol.trim(),
        yth: yth.trim(),
        attn: attn.trim(),
        attnNama: attnNama.trim() || null,
        telp: telp.trim(),
        customerCode: isRegistered ? customerCode : null,
        createdBy: dataUser.name || dataUser.role,
        items: filled.map((i) => ({
          quantity: i.quantity.trim(),
          satuan: i.satuan.trim().toUpperCase(),
          namaBarang: i.namaBarang.replace(/\r\n/g, "\n").trim(),
          keterangan: i.keterangan.trim(),
        })),
      },
      dataUser.auth,
      setCreated,
      ""
    );
  };

  // respon backend sudah ada nomor SJ -> buat PDF
  useEffect(() => {
    if (!createdNoSj) return;
    let cancelled = false;
    (async () => {
      const filled = items
        .filter(
          (i) => i.quantity.trim() || i.namaBarang.trim() || i.keterangan.trim()
        )
        .map((i) => ({ ...i, satuan: i.satuan.trim().toUpperCase() }));
      const blob = await pdf(
        <PdfSuratJalanManual
          noSj={createdNoSj}
          date={date}
          noPol={noPol.trim()}
          yth={yth.trim()}
          attnNama={attnNama.trim()}
          attn={attn.trim()}
          telp={telp.trim()}
          items={filled}
        />
      ).toBlob();
      if (cancelled) return;
      saveAs(
        blob,
        `${safeName(createdNoSj)} ${safeName(yth.trim())} ${moment(date).format(
          "DD-MM-YY"
        )}.pdf`
      );
      setSubmitting(false);
      if (onCreated) onCreated();
      onClose();
    })();
    return () => {
      cancelled = true;
    };
  }, [created]);

  // jaga-jaga kalau server tidak membalas
  useEffect(() => {
    if (!submitting) return;
    const t = setTimeout(() => {
      setSubmitting(false);
      setError(
        "Tidak ada respon dari server. Cek koneksi, lalu coba lagi. Jika sempat tersimpan, nomor SJ tersebut sudah terpakai."
      );
    }, 12000);
    return () => clearTimeout(t);
  }, [submitting]);

  return (
    <PopUpComponent className="po-modal po-modal--lg po-modal--manual">
      <div className="po-modal-head">
        <h3 className="po-modal-title">Buat Surat Jalan Manual</h3>
        <img
          style={{ width: 36, height: 36, cursor: "pointer" }}
          src={CloseLogo}
          alt="tutup"
          onClick={onClose}
        />
      </div>

      <div className="po-form-row">
        <div className="po-field">
          <label className="po-label">Surat Jalan No.</label>
          <input
            className="po-input"
            style={{ width: 170, background: "var(--po-alt)", fontWeight: 700 }}
            value={
              previewNoSj
                ? previewNoSj
                : previewFailed
                ? "gagal memuat"
                : "memuat..."
            }
            readOnly
          />
        </div>
        <div className="po-field">
          <label className="po-label">Cimanggis, (tanggal)</label>
          <input
            className="po-input po-input--date"
            type="date"
            value={date}
            disabled={submitting}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="po-field">
          <label className="po-label">No. Pol</label>
          <input
            className="po-input"
            type="text"
            placeholder="Contoh: B1234ABC"
            value={noPol}
            disabled={submitting}
            onChange={(e) => setNoPol(e.target.value.toUpperCase())}
          />
        </div>
      </div>
      <p className="po-hint">
        Nomor di atas adalah nomor berikutnya. Nomor final dikunci saat tombol
        Buat ditekan, dan urutannya sama dengan Surat Jalan dari Memo Kirim.
      </p>

      {previewFailed && !previewNoSj ? (
        <p className="po-error">
          Nomor SJ tidak bisa dimuat. Pastikan route <b>/suratJalanManual</b>{" "}
          sudah terdaftar di backend dan backend sudah di-restart, lalu ganti
          tanggal sekali untuk memuat ulang.
        </p>
      ) : null}

      <div
        className="po-check"
        onClick={() => !submitting && toggleRegistered()}
      >
        <input type="checkbox" checked={isRegistered} readOnly />
        <label>Tujuan sudah terdaftar (ambil dari data customer)</label>
      </div>

      <div className="po-form-row">
        {isRegistered ? (
          <div className="po-field">
            <label className="po-label">Customer</label>
            <select
              className="po-select"
              value={customerCode}
              disabled={submitting}
              onChange={(e) => pickCustomer(e.target.value)}
            >
              <option value="" disabled>
                Pilih Customer
              </option>
              {dataCustomer.map((c) => (
                <option key={c.customer} value={c.customer}>
                  {c.customer}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="po-field">
          <label className="po-label">Yth.</label>
          <input
            className="po-input"
            style={{ width: 230 }}
            type="text"
            placeholder="Nama perusahaan tujuan"
            value={yth}
            readOnly={isRegistered}
            disabled={submitting}
            onChange={(e) => setYth(e.target.value)}
          />
        </div>
        <div className="po-field">
          <label className="po-label">Attn (nama, opsional)</label>
          <input
            className="po-input"
            style={{ width: 170 }}
            type="text"
            placeholder="Nama penerima"
            value={attnNama}
            disabled={submitting}
            onChange={(e) => setAttnNama(e.target.value)}
          />
        </div>
        <div className="po-field">
          <label className="po-label">Alamat</label>
          <input
            className="po-input"
            style={{ width: 340 }}
            type="text"
            placeholder="Alamat tujuan"
            value={attn}
            readOnly={isRegistered}
            disabled={submitting}
            onChange={(e) => setAttn(e.target.value)}
          />
        </div>
        <div className="po-field">
          <label className="po-label">Telp</label>
          <input
            className="po-input"
            style={{ width: 130 }}
            type="text"
            value={telp}
            readOnly={isRegistered}
            disabled={submitting}
            onChange={(e) => setTelp(e.target.value)}
          />
        </div>
      </div>

      <div className="po-toolbar" style={{ marginTop: 8 }}>
        <label className="po-label" style={{ margin: 0 }}>
          Item barang
        </label>
        <div className="po-toolbar-group">
          <button
            className="po-btn-square"
            disabled={submitting || items.length <= 1}
            onClick={removeItem}
          >
            &#8722;
          </button>
          <button
            className="po-btn-square"
            disabled={submitting}
            onClick={addItem}
          >
            &#43;
          </button>
          <span className="po-summary">
            <b>{items.length}</b> item
          </span>
        </div>
      </div>

      <div className="po-table-wrap" style={{ maxHeight: "36vh" }}>
        <table className="po-table">
          <thead>
            <tr>
              <th className="po-th" style={{ width: 44 }}>
                No.
              </th>
              <th className="po-th" style={{ width: 110 }}>
                Quantity
              </th>
              <th className="po-th" style={{ width: 90 }}>
                Satuan
              </th>
              <th className="po-th">Nama Barang</th>
              <th className="po-th">Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className={`po-row ${i % 2 ? "po-row--alt" : ""}`}>
                <td className="po-td">{i + 1}.</td>
                <td className="po-td">
                  <input
                    className="po-input"
                    style={{ width: 96 }}
                    type="text"
                    inputMode="numeric"
                    placeholder="1000"
                    value={it.quantity}
                    disabled={submitting}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                  />
                </td>
                <td className="po-td">
                  <input
                    className="po-input"
                    style={{ width: 76 }}
                    type="text"
                    placeholder="PCS"
                    value={it.satuan}
                    disabled={submitting}
                    onChange={(e) =>
                      updateItem(i, "satuan", e.target.value.toUpperCase())
                    }
                  />
                </td>
                <td className="po-td">
                  {/* Enter = baris baru. Contoh: baris 1 "( 1 BOX X 1000 PCS )", baris 2 nama barang */}
                  <textarea
                    className="po-textarea po-fill"
                    style={{ minWidth: 240, minHeight: 32, resize: "none" }}
                    rows={2}
                    placeholder={"( 1 BOX X 1000 PCS )\nNama barang"}
                    value={it.namaBarang}
                    disabled={submitting}
                    onChange={(e) =>
                      updateItem(i, "namaBarang", e.target.value.toUpperCase())
                    }
                  />
                </td>
                <td className="po-td">
                  <input
                    className="po-input po-fill"
                    style={{ minWidth: 160 }}
                    type="text"
                    value={it.keterangan}
                    disabled={submitting}
                    onChange={(e) =>
                      updateItem(i, "keterangan", e.target.value)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error ? <p className="po-error">{error}</p> : null}

      <div className="po-actions-end">
        <button
          className="po-btn po-btn--ghost"
          onClick={onClose}
          disabled={submitting}
        >
          Batal
        </button>
        <button
          className="po-btn po-btn--manual"
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? "Membuat..." : "Buat & Download PDF"}
        </button>
      </div>
    </PopUpComponent>
  );
}
